import { LitElement, html, unsafeCSS } from "lit-element"
import { styleMap } from "lit-html/directives/style-map"
import { classMap } from "lit-html/directives/class-map"
import commonStyle from "../common.css"
import style from "./tabs.css"

export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			showArrows: { type: Boolean, attribute: "show-arrows" }
		}
	}

	/** @type {boolean} タブが一度でも選択されたか */
	#firstTabSelected = false

	/** @type {number} バーの親要素からの相対x座標 */
	#barLeft = 0

	/** @type {number} バーの横幅 */
	#barWidth = 0

	/** @type {number} タブのスクロール量 */
	#scrolX = 0

	/** @type {boolean} 左矢印の活性フラグ */
	#leftactiveArrows = false

	/** @type {boolean} 右矢印の活性フラグ */
	#rightactiveArrows = true

	constructor () {
		super()
		this.showArrows = false
	}

	firstUpdated () {
		setTimeout(() => {
			this.shadowRoot.addEventListener("slotchange", (e) => {
				this.updateArrows()
			})
			this.updateArrows()
		})
	}

	updateArrows () {
		// 矢印の活性、非活性を更新
		const box = this.shadowRoot.getElementById("box")
		const overflow = this.shadowRoot.getElementById("overflow")

		// このタイミングで、子タブの追加は画面に描画されていないため、
		// updateCompleteを待ち、描画後にサイズ計算する
		this.updateComplete.then(() => {
			if (box !== null) {
				this.#rightactiveArrows = overflow.clientWidth <= box.scrollWidth
			}
			this.requestUpdate()
		})
	}

	render () {
		const tabs = html`
		<div id="overflow" class="overflow" >
			<div id="box" class="box" style=${this.translateXBox}>
				<div class="tabs">
					<slot @tab-selected="${this.clickTab}" ></slot>
					<div class=${this.tabbarClass} style=${this.selectBarStyle}></div>
				</div>
			</div>
		</div>
		`

		return html`
		${this.showArrows
		? html`
		<div class="overflow-tab">
			<div id="arrows-left" class= ${this.leftarrowsStyle} @click="${this.moveleft}">
				<bui-icon large class="icon icon-button" icon="fas fa-angle-left"></bui-icon>
			</div>
        ${tabs}
			<div id="arrows-right" class= ${this.rightarrowsStyle} @click="${this.moveright}" >
				<bui-icon large class="icon icon-button " icon="fas fa-angle-right"></bui-icon>
			</div>
		</div>`
		: tabs}
		`
	}

	clickTab (e) {
		const myNodeList = this.querySelectorAll("bui-tab")
		myNodeList.forEach(node => {
			if (node !== e.target) {
				node.selected = false
			}
		})
		this.#barLeft = e.target.offsetLeft
		this.#barWidth = e.target.getBoundingClientRect().width
		const overflowWidth = this.shadowRoot.getElementById("overflow").clientWidth
		const boxWidth = this.shadowRoot.getElementById("box").scrollWidth
		// 選択されたタブが画面上の左端だった場合
		if (this.#scrolX >= this.#barLeft) {
			// 選択されたタブがbox内で一番左端だった場合
			if (this.#barLeft === 0) {
				this.#scrolX = 0
				this.#leftactiveArrows = false
			} else {
				this.#scrolX -= this.#barWidth
			}
			this.#rightactiveArrows = true
		}
		// 選択されたタブが画面上の右端だった場合
		if (overflowWidth <= (this.#barLeft + this.#barWidth) - this.#scrolX) {
			// 選択されたタブがbox内の一番右端だった場合
			if (boxWidth === this.#barLeft + this.#barWidth) {
				this.#scrolX = boxWidth - overflowWidth
				this.#rightactiveArrows = false
			} else {
				this.#scrolX += this.#barWidth
			}
			this.#leftactiveArrows = true
		}
		this.requestUpdate().then(() => {
			this.#firstTabSelected = true
		})
	}

	get selectBarStyle () {
		return styleMap({
			width: this.#barWidth + "px",
			left: this.#barLeft + "px"
		})
	}

	get translateXBox () {
		return styleMap({
			transform: "translateX(-" + this.#scrolX + "px)"
		})
	}

	get leftarrowsStyle () {
		return classMap({
			arrows: true,
			"active-arrows": this.#leftactiveArrows,
			"inactive-arrows": !this.#leftactiveArrows
		})
	}

	get rightarrowsStyle () {
		return classMap({
			arrows: true,
			"active-arrows": this.#rightactiveArrows,
			"inactive-arrows": !this.#rightactiveArrows
		})
	}

	get tabbarClass () {
		return classMap({
			"tab-bar": true,
			initialized: this.#firstTabSelected
		})
	}

	/**
	 * 右矢印押下時のタブのスクロール
	 */
	moveright () {
		this.#leftactiveArrows = true
		const box = this.shadowRoot.getElementById("box")
		const overflow = this.shadowRoot.getElementById("overflow")
		if (this.#scrolX + overflow.clientWidth === box.scrollWidth) {
			return
		}
		this.#scrolX += overflow.clientWidth / 2
		if (this.#scrolX + overflow.clientWidth > box.scrollWidth) {
			this.#scrolX = box.scrollWidth - overflow.clientWidth
			this.#rightactiveArrows = false
		}
		this.requestUpdate()
	}

	/**
	 * 左矢印押下時のタブのスクロール
	 */
	moveleft () {
		this.#rightactiveArrows = true
		const overflow = this.shadowRoot.getElementById("overflow")
		if (this.#scrolX <= 0) {
			this.#scrolX = 0
			return
		}
		this.#scrolX -= overflow.clientWidth / 2
		if (this.#scrolX < overflow.clientWidth / 2) {
			this.#scrolX = 0
			this.#leftactiveArrows = false
		}
		this.requestUpdate()
	}
}
